import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { nationsService, categoriesService, ageGroupsService } from "../../services";
import { getAgeGroupColors } from "../../utils/ageGroupColors";
import FlagSelectTailwind from './FlagSelectTailwind';
import GenericSelectTailwind from './GenericSelectTailwind';

const ParticipantFormTailwind = forwardRef(({ 
  participant = null, 
  onSave, 
  onCancel, 
  isSaving = false 
}, ref) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    nation: "",
    nationCode: "",
    age: "",
    ageGroup: "",
    gender: "",
    category: [],
    openFormName: "",
    status: "Ausstehend"
  });
  const [nations, setNations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableOpenForms, setAvailableOpenForms] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [nameInvalid, setNameInvalid] = useState(false);

  // Validate name format (only letters, spaces, hyphens, and umlauts)
  const validateNameFormat = (name) => {
    if (!name || name.trim() === '') return true; // Empty is valid (will be caught by required validation)
    // Allow letters (including umlauts), spaces, hyphens, and apostrophes
    const nameRegex = /^[a-zA-ZäöüÄÖÜß\s\-']+$/;
    return nameRegex.test(name);
  };

  // Check if form is valid (for button disabling)
  const isFormValid = () => {
    // Check name format
    if (!validateNameFormat(formData.name)) {
      return false;
    }
    
    // Check required fields
    if (!formData.name.trim()) return false;
    if (!formData.nationCode) return false;
    if (!formData.age || formData.age < 8 || formData.age > 100) return false;
    if (!formData.gender) return false;
    if (!formData.ageGroup) return false;
    if (formData.category.length === 0) return false;
    
    // Check open form name if open form is selected
    const selectedOpenForm = getSelectedOpenForm();
    if (selectedOpenForm && !formData.openFormName.trim()) return false;
    
    return true;
  };

  // Expose handleSubmit and isFormValid to parent component
  useImperativeHandle(ref, () => ({
    handleSubmit: () => {
      if (validateForm()) {
        // Konvertiere Namen zu IDs für das Backend
        const selectedNation = nations.find(n => n.code === formData.nationCode);
        const nationId = selectedNation ? selectedNation.id : null;
        
        // Konvertiere Kategorienamen zu Kategorie-IDs
        const categoryIds = formData.category.map(categoryName => {
          const category = categories.find(cat => cat.name === categoryName);
          return category ? category.id : null;
        }).filter(id => id !== null);
        
        // Erstelle Backend-kompatible Daten
        const backendData = {
          name: formData.name,
          age: parseInt(formData.age),
          gender: formData.gender,
          status: formData.status,
          nationId: nationId,
          categoryIds: categoryIds,
          openFormName: formData.openFormName
        };
        
        onSave(backendData);
      }
    },
    isFormValid: isFormValid
  }));

  // Lade Referenzdaten
  useEffect(() => {
    loadReferenceData();
  }, []);

  // Initialisiere Form-Daten wenn ein Teilnehmer zum Bearbeiten übergeben wird
  useEffect(() => {
    if (participant) {
      const name = participant.name || "";
      setFormData({
        name: name,
        nation: participant.nation?.name || participant.nation || "",
        nationCode: participant.nation?.code || participant.nationCode || "",
        age: participant.age?.toString() || "",
        ageGroup: participant.ageGroup?.name || participant.ageGroup || "",
        gender: participant.gender || "",
        category: (participant.categories || participant.category || []).map(cat => cat.name || cat),
        openFormName: participant.openFormName || "",
        status: participant.status || "Ausstehend"
      });
      // Reset name validation
      setNameInvalid(false);
    } else {
      // Reset form when no participant (new participant)
      setFormData({
        name: "",
        nation: "",
        nationCode: "",
        age: "",
        ageGroup: "",
        gender: "",
        category: [],
        openFormName: "",
        status: "Ausstehend"
      });
      setNameInvalid(false);
      setErrors({});
    }
  }, [participant]);

  // Aktualisiere verfügbare Kategorien wenn sich Altersgruppe ändert
  useEffect(() => {
    if (formData.ageGroup && categories.length > 0) {
      // Filtere Kategorien basierend auf der Altersgruppe und schließe Partnerformen aus
      const filteredCategories = categories.filter(cat => {
        // Partnerformen ausschließen
        if (cat.type === 'Partnerform') {
          return false;
        }
        // Prüfe ob Altersgruppe in ageGroups enthalten ist (unterstütze sowohl Array von Objekten als auch Strings)
        const ageGroupIds = cat.ageGroups?.map(ag => typeof ag === 'object' ? ag.id : ag) || [];
        const ageGroupNames = cat.ageGroups?.map(ag => typeof ag === 'object' ? ag.name : ag) || [];
        return ageGroupIds.includes(formData.ageGroup) || ageGroupNames.includes(formData.ageGroup);
      });
      
      // Trenne Kategorien nach Typen
      const handForms = filteredCategories.filter(cat => cat.type === "Handform");
      const waffenForms = filteredCategories.filter(cat => cat.type === "Waffenform");
      const openForms = filteredCategories.filter(cat => cat.type === "Offene Form");
      
      setAvailableCategories([...handForms, ...waffenForms]);
      setAvailableOpenForms(openForms);
      
      // Entferne Kategorien aus der Auswahl, die nicht mehr verfügbar sind
      const allAvailable = [...handForms, ...waffenForms, ...openForms];
      const validCategories = formData.category.filter(cat => 
        allAvailable.some(availableCat => availableCat.name === cat)
      );
      
      if (validCategories.length !== formData.category.length) {
        setFormData(prev => ({
          ...prev,
          category: validCategories
        }));
      }
    } else {
      setAvailableCategories([]);
      setAvailableOpenForms([]);
    }
  }, [formData.ageGroup, categories]);

  const loadReferenceData = async () => {
    try {
      const [nationsResponse, categoriesResponse, ageGroupsResponse] = await Promise.all([
        nationsService.getAllNations(),
        categoriesService.getAllCategories(),
        ageGroupsService.getAllAgeGroups()
      ]);

      if (nationsResponse.success) {
        setNations(nationsResponse.data);
      }
      if (categoriesResponse.success) {
        setCategories(categoriesResponse.data);
      }
      if (ageGroupsResponse.success) {
        setAgeGroups(ageGroupsResponse.data);
      }
    } catch (error) {
      console.error("Fehler beim Laden der Referenzdaten:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Fehler für dieses Feld entfernen
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }

    // Validate name format in real-time
    if (field === "name") {
      const isValid = validateNameFormat(value);
      setNameInvalid(!isValid && value.trim() !== '');
    }
  };

  const handleAgeChange = async (age) => {
    const ageNum = parseInt(age);
    
    // Altersgruppe automatisch ermitteln
    let autoAgeGroup = "";
    if (ageNum > 0) {
      try {
        const ageGroupResponse = await ageGroupsService.getAgeGroupForAge(ageNum);
        if (ageGroupResponse.success && ageGroupResponse.data) {
          autoAgeGroup = ageGroupResponse.data.name;
        }
      } catch (error) {
        console.error('Fehler bei der Altersgruppen-Bestimmung:', error);
        // Dynamischer Fallback: Altersgruppen aus der DB laden
        try {
          const allAgeGroups = await ageGroupsService.getAllAgeGroups();
          if (allAgeGroups.success && allAgeGroups.data) {
            const match = allAgeGroups.data.find(
              ag => ageNum >= ag.minAge && ageNum <= ag.maxAge && ag.name !== 'Alle'
            );
            if (match) autoAgeGroup = match.name;
          }
        } catch (fallbackError) {
          console.error('Auch Fallback fehlgeschlagen:', fallbackError);
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      age: age,
      ageGroup: autoAgeGroup
    }));

    // Fehler für diese Felder entfernen
    if (errors.age) {
      setErrors(prev => ({
        ...prev,
        age: ""
      }));
    }
    if (errors.ageGroup) {
      setErrors(prev => ({
        ...prev,
        ageGroup: ""
      }));
    }
  };

  const handleCategoryChange = (categoryName, checked) => {
    setFormData(prev => {
      let newCategories = [...prev.category];
      
      if (checked) {
        // Prüfe ob bereits 3 Kategorien ausgewählt sind (inkl. offene Formen)
        if (newCategories.length >= 3) {
          return prev; // Keine Änderung
        }
        newCategories.push(categoryName);
      } else {
        newCategories = newCategories.filter(cat => cat !== categoryName);
      }
      
      return {
        ...prev,
        category: newCategories
      };
    });
  };

  const handleOpenFormChange = (openFormName, checked) => {
    setFormData(prev => {
      let newCategories = [...prev.category];
      
      // Entferne alle offenen Formen zuerst
      newCategories = newCategories.filter(cat => 
        !availableOpenForms.some(openForm => openForm.name === cat)
      );
      
      if (checked) {
        // Prüfe ob bereits 3 Kategorien ausgewählt sind (inkl. offene Formen)
        if (newCategories.length >= 3) {
          return prev; // Keine Änderung
        }
        // Füge nur die ausgewählte offene Form hinzu
        newCategories.push(openFormName);
      }
      
      return {
        ...prev,
        category: newCategories,
        openFormName: checked ? "" : "" // Reset Form-Name wenn offene Form entfernt wird
      };
    });
  };

  const getSelectedOpenForm = () => {
    return formData.category.find(cat => 
      availableOpenForms.some(openForm => openForm.name === cat)
    );
  };

  const isCategoryDisabled = (categoryName) => {
    const isSelected = formData.category.includes(categoryName);
    const isOpenForm = availableOpenForms.some(openForm => openForm.name === categoryName);
    
    // Wenn bereits 3 Kategorien ausgewählt sind und diese nicht ausgewählt ist
    if (formData.category.length >= 3 && !isSelected) {
      return true;
    }
    
    return false;
  };

  const isOpenFormDisabled = (openFormName) => {
    const isSelected = formData.category.includes(openFormName);
    
    // Wenn bereits 3 Kategorien ausgewählt sind und diese offene Form nicht ausgewählt ist
    if (formData.category.length >= 3 && !isSelected) {
      return true;
    }
    
    return false;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name ist erforderlich";
    }

    if (!formData.nationCode) {
      newErrors.nation = "Nation ist erforderlich";
    }

    if (!formData.age || formData.age < 8 || formData.age > 100) {
      newErrors.age = "Gültiges Alter ist erforderlich (8-100)";
    }

    if (!formData.gender) {
      newErrors.gender = "Geschlecht ist erforderlich";
    }

    if (!formData.ageGroup) {
      newErrors.ageGroup = "Altersgruppe ist erforderlich";
    }

    if (formData.category.length === 0) {
      newErrors.category = "Mindestens eine Kategorie muss ausgewählt werden";
    }

    // Prüfe ob eine offene Form ausgewählt ist und Form-Name erforderlich ist
    const selectedOpenForm = getSelectedOpenForm();
    if (selectedOpenForm && !formData.openFormName.trim()) {
      newErrors.openFormName = "Form-Name ist erforderlich bei offenen Formen";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 gap-3">
        <div className="w-5 h-5 border-2 border-primary dark:border-primary-light border-t-transparent rounded-full animate-spin"></div>
        <span className="text-sm text-slate-500 dark:text-slate-400">{t("admin.loading", "Daten werden geladen…")}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* BASISINFORMATIONEN */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-visible">
        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700/60">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
            <i className="fas fa-user text-[9px]"></i>
            Basisinformationen
          </span>
        </div>
        <div className="p-5 overflow-visible">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                {t("admin.participants.form.name", "Name")} <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`w-full px-3 h-10 border rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors ${
                  nameInvalid || errors.name
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-700 focus:ring-red-500/20 focus:border-red-400'
                    : 'border-slate-200 dark:border-slate-600'
                }`}
                placeholder="Vollständiger Name"
              />
              {(nameInvalid || errors.name) && (
                <p className="mt-1 text-[11px] text-red-500 dark:text-red-400 flex items-center gap-1">
                  <i className="fas fa-exclamation-circle text-[9px]"></i>
                  {errors.name || "Nur Buchstaben, Bindestriche und Leerzeichen erlaubt"}
                </p>
              )}
            </div>

            {/* Alter */}
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                {t("admin.participants.form.age", "Alter")} <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={formData.age}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || /^\d+$/.test(val)) {
                    handleAgeChange(val);
                  }
                }}
                className={`w-full px-3 h-10 border rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors ${
                  errors.age ? 'border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-900/20' : 'border-slate-200 dark:border-slate-600'
                }`}
                placeholder="z. B. 25"
              />
              {/* Inline Altersgruppen-Anzeige */}
              {formData.age && !errors.age && (
                <div className="mt-1.5 flex items-center gap-1.5">
                  {formData.ageGroup ? (
                    <>
                      <i className="fas fa-arrow-right text-[9px] text-slate-400 dark:text-slate-500"></i>
                      <span className="text-[11px] text-slate-500 dark:text-slate-400">Altersgruppe:</span>
                      <span className="text-[11px] font-semibold text-primary dark:text-primary-light">{formData.ageGroup}</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-exclamation-circle text-[9px] text-amber-400"></i>
                      <span className="text-[11px] text-amber-500 dark:text-amber-400">Keine Altersgruppe gefunden</span>
                    </>
                  )}
                </div>
              )}
              {errors.age && (
                <p className="mt-1 text-[11px] text-red-500 dark:text-red-400 flex items-center gap-1">
                  <i className="fas fa-exclamation-circle text-[9px]"></i>
                  {errors.age}
                </p>
              )}
              {errors.ageGroup && !errors.age && (
                <p className="mt-1 text-[11px] text-red-500 dark:text-red-400 flex items-center gap-1">
                  <i className="fas fa-exclamation-circle text-[9px]"></i>
                  {errors.ageGroup}
                </p>
              )}
            </div>

            {/* Nation */}
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                {t("admin.participants.form.nation", "Nation")} <span className="text-red-400">*</span>
              </label>
              <FlagSelectTailwind
                value={formData.nationCode}
                onChange={(value) => {
                  const selectedNation = nations.find(n => n.code === value);
                  setFormData(prev => ({
                    ...prev,
                    nation: selectedNation ? selectedNation.name : "",
                    nationCode: value
                  }));
                }}
                options={nations}
                placeholder="Nation auswählen…"
              />
              {errors.nation && (
                <p className="mt-1 text-[11px] text-red-500 dark:text-red-400 flex items-center gap-1">
                  <i className="fas fa-exclamation-circle text-[9px]"></i>
                  {errors.nation}
                </p>
              )}
            </div>

            {/* Geschlecht */}
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                {t("admin.participants.form.gender", "Geschlecht")} <span className="text-red-400">*</span>
              </label>
              <GenericSelectTailwind
                value={formData.gender}
                onChange={(value) => handleInputChange("gender", value)}
                options={[
                  { value: "männlich", label: t("admin.participants.gender.male", "Männlich"), icon: <i className="fas fa-mars text-blue-500"></i> },
                  { value: "weiblich", label: t("admin.participants.gender.female", "Weiblich"), icon: <i className="fas fa-venus text-pink-500"></i> }
                ]}
                placeholder={t("admin.participants.form.selectGender", "Geschlecht wählen…")}
                leftIcon={<i className="fas fa-venus-mars text-slate-400 dark:text-slate-500"></i>}
              />
              {errors.gender && (
                <p className="mt-1 text-[11px] text-red-500 dark:text-red-400 flex items-center gap-1">
                  <i className="fas fa-exclamation-circle text-[9px]"></i>
                  {errors.gender}
                </p>
              )}
            </div>

            {/* Status */}
            <div className="md:col-span-2">
              <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                {t("admin.participants.form.status", "Status")}
              </label>
              <GenericSelectTailwind
                value={formData.status}
                onChange={(value) => handleInputChange("status", value)}
                options={[
                  { value: "Ausstehend", label: t("admin.participants.filters.pending", "Ausstehend"), icon: <i className="fas fa-clock text-amber-500"></i> },
                  { value: "Bestätigt", label: t("admin.participants.filters.confirmed", "Bestätigt"), icon: <i className="fas fa-check-circle text-emerald-500"></i> }
                ]}
                placeholder={t("admin.participants.form.selectStatus", "Status wählen…")}
                leftIcon={<i className="fas fa-info-circle text-slate-400 dark:text-slate-500"></i>}
              />
            </div>
          </div>
        </div>
      </div>

      {/* KATEGORIEN */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-visible">
        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700/60 flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
            <i className="fas fa-list text-[9px]"></i>
            Kategorien
          </span>
          {formData.ageGroup && (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-400 dark:text-slate-500 tabular-nums">
                {formData.category.length}/3 gewählt
              </span>
              <div className="flex gap-0.5">
                {[1, 2, 3].map(i => (
                  <div key={i} className={`w-4 h-1.5 rounded-full transition-all ${i <= formData.category.length ? 'bg-primary dark:bg-primary-light' : 'bg-slate-200 dark:bg-slate-700'}`} />
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="p-5 overflow-visible">
          {/* Hinweis Partnerform/Gruppenperformance — kompakt */}
          <div className="mb-4 px-3 py-2.5 bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700/50 rounded-xl space-y-1.5">
            <div className="flex items-center gap-2">
              <i className="fas fa-handshake text-[10px] text-slate-400 dark:text-slate-500 flex-shrink-0 w-3.5 text-center"></i>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                <span className="font-semibold text-slate-600 dark:text-slate-300">Partnerform</span>
                {" "}→ Teilnehmerverwaltung <span className="text-slate-400 dark:text-slate-500">· Partner-Teams Button</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <i className="fas fa-users text-[10px] text-slate-400 dark:text-slate-500 flex-shrink-0 w-3.5 text-center"></i>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                <span className="font-semibold text-slate-600 dark:text-slate-300">Gruppenperformance</span>
                {" "}→ Nationenverwaltung <span className="text-slate-400 dark:text-slate-500">· Team-Verwaltung Button</span>
              </p>
            </div>
          </div>

          {formData.ageGroup ? (() => {
            const handForms = availableCategories.filter(cat => cat.type === "Handform");
            const waffenForms = availableCategories.filter(cat => cat.type === "Waffenform");
            const youthColors = getAgeGroupColors('Jugend');

            const renderChip = (category, isOpenForm = false) => {
              const isChecked = formData.category.includes(category.name);
              const isDisabled = isOpenForm ? isOpenFormDisabled(category.name) : isCategoryDisabled(category.name);
              const toggle = () => {
                if (isDisabled) return;
                if (isOpenForm) handleOpenFormChange(category.name, !isChecked);
                else handleCategoryChange(category.name, !isChecked);
              };

              return (
                <div
                  key={category.id}
                  onClick={toggle}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-all select-none ${
                    isDisabled
                      ? 'bg-slate-50 dark:bg-slate-700/30 border-slate-200 dark:border-slate-700/50 text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-50'
                      : isChecked
                        ? `${youthColors.bgLight} ${youthColors.text} ${youthColors.border} dark:border-blue-400/30 cursor-pointer shadow-sm`
                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-primary/40 dark:hover:border-primary-light/40 cursor-pointer'
                  }`}
                >
                  {isChecked && <i className="fas fa-check text-[9px]"></i>}
                  <span>{category.name}</span>
                </div>
              );
            };

            return (
              <div className="space-y-4">
                {handForms.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-2">Handformen</p>
                    <div className="flex flex-wrap gap-2">{handForms.map(c => renderChip(c))}</div>
                  </div>
                )}
                {waffenForms.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-2">Waffenformen</p>
                    <div className="flex flex-wrap gap-2">{waffenForms.map(c => renderChip(c))}</div>
                  </div>
                )}
                {availableOpenForms.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-2 flex items-center gap-1.5">
                      Offene Formen
                      <span className="normal-case font-normal text-slate-400 dark:text-slate-500">(optional, max. 1)</span>
                    </p>
                    <div className="flex flex-wrap gap-2">{availableOpenForms.map(c => renderChip(c, true))}</div>

                    {getSelectedOpenForm() && (
                      <div className="mt-3">
                        <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                          Form-Name für {getSelectedOpenForm()} <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.openFormName}
                          onChange={(e) => handleInputChange("openFormName", e.target.value)}
                          className={`w-full px-3 h-10 border rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors ${
                            errors.openFormName ? 'border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-900/20' : 'border-slate-200 dark:border-slate-600'
                          }`}
                          placeholder="Name der eigenen Form eingeben…"
                        />
                        {errors.openFormName && (
                          <p className="mt-1 text-[11px] text-red-500 dark:text-red-400 flex items-center gap-1">
                            <i className="fas fa-exclamation-circle text-[9px]"></i>
                            {errors.openFormName}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })() : (
            <div className="flex items-center gap-2.5 py-6 text-center justify-center">
              <i className="fas fa-arrow-up text-[11px] text-slate-300 dark:text-slate-600"></i>
              <p className="text-sm text-slate-400 dark:text-slate-500">
                Alter eingeben, um verfügbare Kategorien zu sehen
              </p>
            </div>
          )}
          {errors.category && (
            <p className="mt-2 text-[11px] text-red-500 dark:text-red-400 flex items-center gap-1">
              <i className="fas fa-exclamation-circle text-[9px]"></i>
              {errors.category}
            </p>
          )}
        </div>
      </div>

      {/* Vorschau */}
      {formData.name && formData.nationCode && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700/60">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
              <i className="fas fa-eye text-[9px]"></i>
              Vorschau
            </span>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-shrink-0">
                {formData.nationCode && (
                  <img
                    src={`${process.env.PUBLIC_URL}/images/flags/${formData.nationCode}.png`}
                    alt=""
                    className="w-10 h-10 object-cover rounded-full border-2 border-slate-200 dark:border-slate-600"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                {formData.gender && (
                  <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border border-white dark:border-slate-800 ${formData.gender === 'weiblich' ? 'bg-pink-100 dark:bg-pink-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                    <i className={`fas text-[8px] ${formData.gender === 'weiblich' ? 'fa-venus text-pink-500' : 'fa-mars text-blue-500'}`}></i>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{formData.name}</div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {formData.nation && <span className="text-xs text-slate-500 dark:text-slate-400">{formData.nation}</span>}
                  {formData.age && <span className="text-[11px] text-slate-400 dark:text-slate-500">{formData.age} J.</span>}
                  {formData.ageGroup && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 dark:bg-primary-light/10 text-primary dark:text-primary-light border border-primary/20 dark:border-primary-light/20">
                      {formData.ageGroup}
                    </span>
                  )}
                </div>
              </div>
              {formData.status && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border flex-shrink-0 ${
                  formData.status === 'Bestätigt'
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-700/50'
                    : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-700/50'
                }`}>
                  <i className={`fas text-[8px] ${formData.status === 'Bestätigt' ? 'fa-check' : 'fa-clock'}`}></i>
                  {formData.status}
                </span>
              )}
            </div>
            {formData.category.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700/60 flex flex-wrap gap-1.5">
                {formData.category.map((cat, index) => (
                  <span key={index} className="inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-600">
                    {cat}
                    {formData.openFormName && availableOpenForms.some(f => f.name === cat) && (
                      <span className="ml-1 text-slate-400 dark:text-slate-500">· {formData.openFormName}</span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    
  );
});

export default ParticipantFormTailwind;